import { useState, useEffect, useCallback } from 'react';
import { loginApi } from '../services/api';
import type { AppInfo } from '../types/auth';

interface LoginResult {
  success: boolean;
  hasExistingSession?: boolean;
  sessionToken?: string;
  error?: string;
  errorCode?: string;
  apps?: AppInfo[];
  access_token?: string;
}

export function useAuth() {
  const [token, setToken] = useState<string | null>(null);
  const [apps, setApps] = useState<AppInfo[]>([]);

  useEffect(() => {
    const savedToken = localStorage.getItem('access_token');
    const savedApps = localStorage.getItem('apps');

    if (savedToken) {
      setToken(savedToken);
    }
    if (savedApps) {
      try {
        setApps(JSON.parse(savedApps));
      } catch {
        setApps([]);
      }
    }
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<LoginResult> => {
    const response = await loginApi({ username, password });

    if (response.errorCode) {
      return { success: false, error: response.message, errorCode: response.errorCode };
    }

    if (response.sessionToken) {
      return {
        success: false,
        hasExistingSession: true,
        sessionToken: response.sessionToken,
      };
    }

    if (response.access_token) {
      setToken(response.access_token);
      setApps(response.apps || []);
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('apps', JSON.stringify(response.apps || []));
    }

    return { success: true, apps: response.apps, access_token: response.access_token };
  }, []);

  const expireAndRelogin = useCallback(async (_sessionToken: string, username: string, password: string): Promise<LoginResult> => {
    const response = await loginApi({ username, password, force: true });

    if (response.errorCode) {
      return { success: false, error: response.message, errorCode: response.errorCode };
    }

    if (response.access_token) {
      setToken(response.access_token);
      setApps(response.apps || []);
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('apps', JSON.stringify(response.apps || []));
    }

    return { success: true, apps: response.apps, access_token: response.access_token };
  }, []);

  return { token, apps, login, expireAndRelogin };
}

export function redirectAfterLogin(access_token: string, apps?: AppInfo[]): boolean {
  if (apps && apps.length === 1) {
    const lang = localStorage.getItem('language') || 'fr';
    const url = `${apps[0].redirect_uri}?token=${access_token}&lang=${lang}`;
    localStorage.setItem('last_session', url);
    window.location.href = url;
    return true;
  }
  return false;
}
